import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchCmsItems, getOptimizedImageUrl } from '../lib/cms'
import InquiryModal from '../components/InquiryModal'
import AnimatedStat from '../components/AnimatedStat'

export default function HomePage() {
  const [modalOpen, setModalOpen] = useState(false)

  // Direct contact form state
  const [contactForm, setContactForm] = useState({
    navn: '',
    firma: '',
    email: '',
    telefon: '',
    besked: '',
    website_url: '', // Honeypot field
  })
  const [contactLoading, setContactLoading] = useState(false)
  const [contactSubmitted, setContactSubmitted] = useState(false)

  useEffect(() => {
    document.title = 'SKI & CO TRAVEL · SKIREJSER FOR ERHVERVSLIVET SIDEN 2014'
  }, [])

  const handleContactChange = (e) => {
    setContactForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleContactSubmit = async (e) => {
    e.preventDefault()
    setContactLoading(true)
    try {
      const payload = {
        navn: contactForm.navn,
        virksomhed: contactForm.firma,
        email: contactForm.email,
        telefonnummer: contactForm.telefon,
        beskrivelse: contactForm.besked,
        website_url: contactForm.website_url,
        modtaget_dato: new Date().toISOString().split('T')[0],
      }

      await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      setContactSubmitted(true)
    } catch (err) {
      console.warn('Webform submission notice:', err)
      setContactSubmitted(true)
    } finally {
      setContactLoading(false)
    }
  }

  const destinationsList = [
    { name: 'St. Anton', land: 'ØSTRIG', hoejde: '1.304 → 2.811 M', slug: 'st-anton' },
    { name: 'Sölden', land: 'ØSTRIG', hoejde: '1.377 → 3.340 M', slug: 'soelden' },
    { name: 'Saalbach–Hinterglemm', land: 'ØSTRIG', hoejde: '1.003 → 2.096 M', slug: 'saalbach' },
    { name: 'Ischgl', land: 'ØSTRIG', hoejde: '1.377 → 2.872 M', slug: 'ischgl' },
    { name: 'Zauchensee', land: 'ØSTRIG', hoejde: '1.000 → 2.188 M', slug: 'zauchensee' },
    { name: 'Schladming', land: 'ØSTRIG', hoejde: '745 → 2.015 M', slug: 'schladming' },
    { name: 'Canazei', land: 'ITALIEN', hoejde: '1.465 → 2.640 M', slug: 'canazei' },
  ]

  const konceptCards = [
    {
      titel: 'Produktseminar',
      beskrivelse: 'Saml salgsteamet om lanceringen i rammer, der matcher ambitionerne.',
      icon: 'fa-box-open',
    },
    {
      titel: 'Salgsmøder',
      beskrivelse: 'Få mere ud af kvartalsmødet med en dagsorden, der rykker udenfor kontoret.',
      icon: 'fa-chart-line',
    },
    {
      titel: 'Value-creation',
      beskrivelse: 'Skab rum til de strategiske samtaler, der sjældent opstår ved skrivebordet.',
      icon: 'fa-lightbulb',
    },
    {
      titel: 'Workshops',
      beskrivelse: 'Kombiner fagligt indhold med aktiviteter, der bygger sammenhold.',
      icon: 'fa-people-group',
    },
    {
      titel: 'Netværksmøder',
      beskrivelse: 'Bring kunder og kolleger sammen om en oplevelse, de husker meget lang tid.',
      icon: 'fa-handshake',
    },
  ]

  const procesSteps = [
    {
      nummer: '01',
      titel: 'Behovsafklaring',
      beskrivelse: 'Vi afstemmer formål, budget og antal deltagere.',
    },
    {
      nummer: '02',
      titel: 'Skræddersyet program',
      beskrivelse: 'I vælger indhold og prioriteringer — vi sætter det sammen.',
    },
    {
      nummer: '03',
      titel: 'Gennemførelse',
      beskrivelse: 'Vi er med hele vejen, så I kan fokusere på jeres gæster.',
    },
    {
      nummer: '04',
      titel: 'Opfølgning',
      beskrivelse: 'Evaluering og inspiration til næste arrangement.',
    },
  ]

  return (
    <div className="space-y-20 pb-20 overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-slate-950">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl(
              'https://assets.auxodev.dk/assets/9bff8fd4-e9da-49ba-96b9-18c20ed7b396/hero-ski.png',
              1920,
              85
            )}
            alt="Skirejser i Alperne - SKI & CO TRAVEL"
            className="w-full h-full object-cover object-center scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/75 to-slate-950/40" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-24 text-center space-y-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/90 border border-slate-700/80 text-cyan-300 text-xs sm:text-sm font-semibold backdrop-blur-md shadow-xl">
            <i className="fa-solid fa-mountain-snow" aria-hidden="true" />
            <span>SKI & CO TRAVEL · SKIREJSER FOR ERHVERVSLIVET SIDEN 2014</span>
          </div>

          <h1 className="font-heading font-extrabold text-3xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.15] drop-shadow-md">
            Netværk, der rækker længere end pisten
          </h1>

          <p className="max-w-3xl mx-auto text-lg sm:text-xl text-slate-200 font-normal leading-relaxed drop-shadow-sm">
            Vi planlægger og gennemfører skræddersyede skiture til produktseminarer, salgsmøder, workshops og netværksarrangementer — så I kan koncentrere jer om relationerne, mens vi klarer resten.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
            <a
              href="#kontakt"
              className="px-8 py-4 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-base transition-all transform hover:-translate-y-0.5 shadow-xl shadow-cyan-500/25 flex items-center gap-2"
            >
              <i className="fa-solid fa-paper-plane" aria-hidden="true" />
              <span>Lad os planlægge jeres næste tur</span>
            </a>

            <button
              onClick={() => setModalOpen(true)}
              className="px-8 py-4 rounded-xl bg-slate-900/90 hover:bg-slate-800 text-white font-bold text-base border border-slate-700/80 backdrop-blur-md transition-all transform hover:-translate-y-0.5 shadow-xl flex items-center gap-2"
            >
              <i className="fa-solid fa-envelope" aria-hidden="true" />
              <span>Send en forespørgsel</span>
            </button>
          </div>

          <div className="pt-6 flex items-center justify-center gap-4 text-sm text-slate-300">
            <span className="text-slate-400">Direkte rådgivning:</span>
            <a
              href="tel:+4551589452"
              className="font-bold text-white hover:text-cyan-400 transition-colors inline-flex items-center gap-2 underline underline-offset-4 decoration-cyan-500/50"
            >
              <i className="fa-solid fa-phone text-cyan-400" aria-hidden="true" />
              <span>+45 51 58 94 52</span>
            </a>
          </div>
        </div>
      </section>

      {/* OM SKI & CO TRAVEL */}
      <section id="om-os" className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 sm:p-12 shadow-2xl relative overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-cyan-400">
                OM SKI & CO TRAVEL
              </span>
              <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-white">
                Grundlagt af en ivrig skiløber. Bygget til erhvervslivet.
              </h2>
              <div className="space-y-4 text-slate-300 leading-relaxed text-sm sm:text-base">
                <p>
                  Ski & Co Travel blev grundlagt i 2014 af Claus Nexø Hansen — ivrig skiløber gennem 25 år og tidligere salgschef/salgsdirektør. I de roller stod han selv for at arrangere firmaers B2B-skiferier for deres kunder, og den erfaring er nu fundamentet for de rejser, vi sætter sammen til jer.
                </p>
                <p>
                  Vi holder til i hele Alperne — Østrig, Frankrig og Italien — men har naturligvis vores egne favoritter, som vi vender tilbage til år efter år.
                </p>
                <p>
                  Alt kan skræddersys: transport med bus, fly eller kør-selv, indkvartering på 5-stjernede hoteller eller lejligheder med morgenmad, gourmetrestauranter samt salgscoaches eller foredragsholdere. I bestemmer indhold og prioritet — vi sørger for, at det hænger sammen.
                </p>
              </div>

              {/* Animated Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-slate-800">
                <AnimatedStat value="2014" label="Grundlagt" />
                <AnimatedStat value="3" label="Lande i Alperne" />
                <AnimatedStat value="35+" label="År på ski" />
                <AnimatedStat value="100%" label="Skræddersyet" />
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative rounded-2xl overflow-hidden border border-slate-800 shadow-2xl bg-slate-950">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/9bff8fd4-e9da-49ba-96b9-18c20ed7b396/im1.jpeg"
                  alt="Claus Nexø Hansen og teamet bag Ski & Co Travel."
                  className="w-full h-80 sm:h-96 object-cover object-top"
                />
                <div className="p-4 bg-slate-950 border-t border-slate-800 text-center">
                  <p className="text-xs font-medium text-slate-300">
                    Claus Nexø Hansen og teamet bag Ski & Co Travel.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* KONCEPTET */}
      <section id="konceptet" className="max-w-7xl mx-auto px-4 sm:px-6 space-y-10">
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <span className="text-xs font-bold uppercase tracking-widest text-cyan-400">
            KONCEPTET
          </span>
          <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-white">
            Mere end en skitur
          </h2>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
            Hvert år afholdes der tusindvis af møder rundt om i landet — de færreste huskes i ret lang tid. Et fælles arrangement i naturskønne omgivelser gør. Det er derfor, vi skræddersyer hvert arrangement, så det matcher formålet.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {konceptCards.map((card, idx) => (
            <div
              key={idx}
              className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-3 hover:border-cyan-500/50 transition-colors shadow-xl"
            >
              <div className="w-12 h-12 rounded-xl bg-cyan-500/10 text-cyan-400 flex items-center justify-center text-xl">
                <i className={`fa-solid ${card.icon}`} aria-hidden="true" />
              </div>
              <h3 className="font-heading font-bold text-lg text-white">
                {card.titel}
              </h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                {card.beskrivelse}
              </p>
            </div>
          ))}

          {/* Highlights Card */}
          <div className="bg-gradient-to-br from-cyan-950/60 to-slate-900 border border-cyan-800/60 rounded-2xl p-6 flex flex-col justify-between shadow-xl">
            <div className="space-y-3">
              <span className="text-xs font-bold uppercase tracking-wider text-cyan-300">
                Alt i én løsning
              </span>
              <p className="text-slate-200 text-sm leading-relaxed font-medium">
                Kort sagt: vi arrangerer alt fra transport, indkvartering og mødefaciliteter til foredragsholdere og gourmetoplevelser — jeres garanti for et arrangement, der huskes længe.
              </p>
            </div>
            <button
              onClick={() => setModalOpen(true)}
              className="mt-6 w-full py-3 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-sm transition-colors text-center"
            >
              Send os jeres rammer &rarr;
            </button>
          </div>
        </div>
      </section>

      {/* DESTINATIONER - Clean List Styling */}
      <section id="destinationer" className="bg-slate-950 py-16 border-y border-slate-800/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-10">
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <span className="text-xs font-bold uppercase tracking-widest text-cyan-400">
              DESTINATIONER
            </span>
            <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-white">
              Vores favoritter i Alperne
            </h2>
          </div>

          {/* Nice List Styling */}
          <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-2xl divide-y divide-slate-800/80">
            {destinationsList.map((dest, idx) => (
              <div
                key={idx}
                className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-800/50 transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-cyan-950/80 border border-cyan-800/60 text-cyan-400 flex items-center justify-center shrink-0 font-mono text-xs font-bold">
                    0{idx + 1}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-cyan-950 text-cyan-300 border border-cyan-800/60 uppercase tracking-wider">
                        {dest.land}
                      </span>
                    </div>
                    <h3 className="font-heading font-bold text-lg text-white group-hover:text-cyan-300 transition-colors">
                      {dest.name}
                    </h3>
                  </div>
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-6 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-800/60">
                  <div className="text-left sm:text-right">
                    <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold block">
                      Piste- & Lifthøjde
                    </span>
                    <span className="text-sm text-cyan-300 font-mono font-bold flex items-center gap-1.5">
                      <i className="fa-solid fa-mountain-snow text-cyan-400 text-xs" aria-hidden="true" />
                      {dest.hoejde}
                    </span>
                  </div>

                  <Link
                    to={`/destinationer/${dest.slug}`}
                    className="px-4 py-2 rounded-xl bg-slate-800 group-hover:bg-cyan-500 group-hover:text-slate-950 text-slate-200 font-semibold text-xs sm:text-sm flex items-center gap-2 transition-all shrink-0 shadow-md"
                  >
                    <span>Udforsk</span>
                    <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-xs sm:text-sm text-slate-400 max-w-3xl mx-auto leading-relaxed border-t border-slate-800/80 pt-6">
            Første tal er byens/dalstationens højde, andet tal er den højeste skiheis i området — et fingerpeg om terrænet. Vi tager lige så gerne fat på jeres ønskedestination andre steder i Alperne.
          </p>
        </div>
      </section>

      {/* PROCESSEN - Full Screen Width */}
      <section id="processen" className="w-full bg-slate-950 py-16 sm:py-20 border-y border-slate-800/80">
        <div className="w-full px-4 sm:px-8 lg:px-12 space-y-12">
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <span className="text-xs font-bold uppercase tracking-widest text-cyan-400">
              PROCESSEN
            </span>
            <h2 className="font-heading font-extrabold text-2xl sm:text-4xl text-white">
              Fra idé til afgang
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 w-full">
            {procesSteps.map((step, idx) => (
              <div
                key={idx}
                className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 space-y-4 relative overflow-hidden shadow-2xl hover:border-cyan-500/50 transition-all group"
              >
                <div className="flex items-center justify-between">
                  <span className="font-heading font-black text-4xl sm:text-5xl text-cyan-400/40 group-hover:text-cyan-400 transition-colors">
                    {step.nummer}
                  </span>
                  <div className="w-8 h-8 rounded-full bg-cyan-500/10 text-cyan-400 flex items-center justify-center text-xs font-bold">
                    <i className="fa-solid fa-check" aria-hidden="true" />
                  </div>
                </div>
                <h3 className="font-heading font-bold text-lg text-white">
                  {step.titel}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {step.beskrivelse}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* KONTAKT SECTION */}
      <section id="kontakt" className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 sm:p-12 shadow-2xl space-y-8">
          <div className="space-y-2">
            <span className="text-xs font-bold uppercase tracking-widest text-cyan-400">
              KONTAKT
            </span>
            <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-white">
              Lad os planlægge jeres næste tur
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Contact Person Info */}
            <div className="lg:col-span-5 space-y-6">
              <div className="p-6 rounded-2xl bg-slate-950 border border-slate-800 space-y-4">
                <div>
                  <h3 className="font-heading font-bold text-lg text-white">
                    Claus Nexø Hansen
                  </h3>
                  <p className="text-sm text-cyan-400 font-medium">
                    Grundlægger, Ski & Co Travel
                  </p>
                </div>

                <div className="space-y-3 pt-2 text-sm">
                  <div>
                    <a
                      href="mailto:claus@skiogco.dk"
                      className="inline-flex items-center gap-2.5 text-slate-200 hover:text-cyan-400 transition-colors whitespace-nowrap font-medium"
                    >
                      <i className="fa-solid fa-envelope text-cyan-400" aria-hidden="true" />
                      <span>claus@skiogco.dk</span>
                    </a>
                  </div>
                  <div>
                    <a
                      href="tel:+4551589452"
                      className="inline-flex items-center gap-2.5 text-slate-200 hover:text-cyan-400 transition-colors whitespace-nowrap font-medium"
                    >
                      <i className="fa-solid fa-phone text-cyan-400" aria-hidden="true" />
                      <span>+45 51 58 94 52</span>
                    </a>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-slate-950/60 border border-slate-800/80 text-xs text-slate-400 leading-relaxed space-y-2">
                <p className="text-slate-300 font-semibold">
                  SKI & CO TRAVEL ApS
                </p>
                <p>Kærmindevej 31, 6000 Kolding</p>
                <p>Vi glæder os til at høre fra jer og sammensætte et unikt arrangement.</p>
              </div>
            </div>

            {/* Direct Contact Form */}
            <div className="lg:col-span-7">
              {contactSubmitted ? (
                <div className="bg-emerald-950/40 border border-emerald-800 p-8 rounded-2xl text-center space-y-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 flex items-center justify-center mx-auto text-xl">
                    <i className="fa-solid fa-check" aria-hidden="true" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-white">
                    Tak for din henvendelse!
                  </h3>
                  <p className="text-slate-300 text-sm max-w-md mx-auto leading-relaxed">
                    Vi har modtaget din besked og vender tilbage hurtigst muligt.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-4 text-sm">
                  {/* Honeypot */}
                  <input
                    type="text"
                    name="website_url"
                    value={contactForm.website_url}
                    onChange={handleContactChange}
                    className="sr-only"
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1">
                        Navn <span className="text-cyan-400">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        name="navn"
                        value={contactForm.navn}
                        onChange={handleContactChange}
                        placeholder="Navn"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1">
                        Firma
                      </label>
                      <input
                        type="text"
                        name="firma"
                        value={contactForm.firma}
                        onChange={handleContactChange}
                        placeholder="Firma"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1">
                        Email <span className="text-cyan-400">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        name="email"
                        value={contactForm.email}
                        onChange={handleContactChange}
                        placeholder="Email"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1">
                        Telefon <span className="text-cyan-400">*</span>
                      </label>
                      <input
                        type="tel"
                        required
                        name="telefon"
                        value={contactForm.telefon}
                        onChange={handleContactChange}
                        placeholder="Telefon"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">
                      Besked
                    </label>
                    <textarea
                      rows="4"
                      name="besked"
                      value={contactForm.besked}
                      onChange={handleContactChange}
                      placeholder="Besked"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={contactLoading}
                    className="w-full py-3.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-base transition-colors shadow-lg shadow-cyan-500/25 flex items-center justify-center gap-2"
                  >
                    {contactLoading ? (
                      <>
                        <i className="fa-solid fa-spinner animate-spin" aria-hidden="true" />
                        <span>Sender...</span>
                      </>
                    ) : (
                      <span>Send henvendelse</span>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Modal Inquiry */}
      <InquiryModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  )
}
